/* Sandbox Chats (ADR 0066, bigdayz.new) — pure helpers for the Sandbox tab.
   A Sandbox Chat is a fabricated Sandbox Fan inside this real Account; the real turn
   answers it and nothing reaches the Platform. The transcript comes from the brain
   as rows: fan / model messages, plus `system` rows that record what the turn would
   have done outside the chat (a notification, a list change, mark-unread, a fan note). */

import type { SandboxMessage, SandboxSeedMessage } from '../../../../api/client';

/** The brain holds at most this many open Sandbox Chats per Account. */
export const MAX_OPEN_CHATS = 30;

/* ── Starting points ─────────────────────────────────────────────────
   A seed is the history before the first live turn, and the only way to give a
   Sandbox Fan money history. Spend is bought PPVs plus tips. */
export type PresetId = 'new' | 'regular' | 'spender';

const ppv = (price: number, text: string): SandboxSeedMessage => ({ sender: 'model', text, price, purchased: true });
const tip = (amount: number, text: string): SandboxSeedMessage => ({ sender: 'fan', text, tip: amount });
const fan = (text: string): SandboxSeedMessage => ({ sender: 'fan', text });
const model = (text: string): SandboxSeedMessage => ({ sender: 'model', text });

export const PRESETS: Record<PresetId, SandboxSeedMessage[]> = {
  new: [],
  regular: [
    fan('hey babe, missed you'),
    model('heyy you 😘 missed you too'),
    ppv(40, 'a little something from this morning 🙈'),
    fan('wow you look unreal'),
    ppv(60, 'since you liked the last one…'),
    tip(40, 'for being amazing'),
    model('omg thank you babe 🥰'),
  ],
  spender: [
    fan('there she is 😍'),
    model('my favourite is back 🥺'),
    ppv(300, 'made this one just for you'),
    tip(500, 'you deserve it'),
    ppv(500, 'the full set, like you asked 😏'),
    tip(1000, 'best girl on here'),
    model('you spoil me so much 🙈'),
  ],
};
export const PRESET_IDS: PresetId[] = ['new', 'regular', 'spender'];

export const seedSpend = (seed: SandboxSeedMessage[]) =>
  seed.reduce((sum, m) => sum + (m.purchased ? m.price ?? 0 : 0) + (m.tip ?? 0), 0);

/* ── Money ───────────────────────────────────────────────────────── */
export const num = (v: number | string | null | undefined) => {
  const n = typeof v === 'string' ? Number(v) : v ?? 0;
  return Number.isFinite(n) ? (n as number) : 0;
};

export const money = (v: number) =>
  '$' + v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 });

const isPpv = (m: SandboxMessage) => m.sender === 'model' && num(m.price) > 0;

/** What the Sandbox Fan has paid in this chat: bought PPVs plus tips. */
export function spentOf(msgs: SandboxMessage[]): number {
  return msgs.reduce((sum, m) => {
    if (isPpv(m) && m.is_purchased) return sum + num(m.price);
    if (m.sender === 'fan') return sum + num(m.tip_amount);
    return sum;
  }, 0);
}

export function offersOf(msgs: SandboxMessage[]) {
  const sent = msgs.filter(isPpv);
  return { sent: sent.length, unlocked: sent.filter((m) => m.is_purchased).length };
}

/* ── Transcript → rendered rows ──────────────────────────────────── */
export interface MediaMix { photo: number; video: number; audio: number }

export type Row =
  | { t: 'fan'; id: number | string; text: string; tip: number; tight: boolean; pending?: boolean }
  | { t: 'model'; id: number | string; text: string; mix: MediaMix | null; price: number; purchased: boolean; deleted: boolean; tight: boolean; turnId: string | null; endsTurn: boolean }
  | { t: 'sys'; id: number | string; kind: string; payload: Record<string, unknown> };

export function mixOf(media: SandboxMessage['media']): MediaMix | null {
  if (!media || !media.length) return null;
  const mix: MediaMix = { photo: 0, video: 0, audio: 0 };
  for (const m of media) {
    const kind = (m.type || 'photo').toLowerCase();
    if (kind === 'video' || kind === 'gif') mix.video += 1;
    else if (kind === 'audio') mix.audio += 1;
    else mix.photo += 1;
  }
  return mix;
}

/** Rows for the chat pane. `tight` marks a bubble that continues its sender's run;
 *  `endsTurn` marks the last bubble of a run its turn wrote (where its trace opens). */
export function rowsOf(msgs: SandboxMessage[], showSystem: boolean): Row[] {
  const rows: Row[] = [];
  let prev: string | null = null;
  const said = msgs.filter((m) => m.sender !== 'system');
  const nextSaid = new Map(said.map((m, i) => [m.id, said[i + 1]]));
  for (const m of msgs) {
    if (m.sender === 'system') {
      prev = null;
      if (showSystem) rows.push({ t: 'sys', id: m.id, kind: m.kind || '', payload: m.payload || {} });
      continue;
    }
    const tight = prev === m.sender;
    prev = m.sender;
    if (m.sender === 'fan') {
      rows.push({ t: 'fan', id: m.id, text: m.text || '', tip: num(m.tip_amount), tight, pending: m.id < 0 });
    } else {
      rows.push({
        t: 'model', id: m.id, text: m.text || '', mix: mixOf(m.media), price: num(m.price),
        purchased: !!m.is_purchased, deleted: !!m.deleted_at, tight,
        turnId: m.turn_id ?? null,
        endsTurn: !!m.turn_id && (nextSaid.get(m.id)?.sender !== 'model' || nextSaid.get(m.id)?.turn_id !== m.turn_id),
      });
    }
  }
  return rows;
}

/* An optimistic fan row has a negative id: -(its message id) once the brain has
   accepted it, a large negative placeholder before. It sorts by |id| and is dropped
   as soon as the server's copy arrives. */
const order = (m: SandboxMessage) => Math.abs(m.id);
const settled = (byId: Map<number, SandboxMessage>) => (m: SandboxMessage) => m.id > 0 || !byId.has(-m.id);

/** Merge polled rows into the transcript: newer copies replace older ones, id order kept. */
export function mergeMessages(cur: SandboxMessage[], incoming: SandboxMessage[]): SandboxMessage[] {
  if (!incoming.length) return cur;
  const byId = new Map(cur.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].filter(settled(byId)).sort((a, b) => order(a) - order(b));
}

/** A full re-read replaces the transcript, keeping optimistic rows the server lacks. */
export function replaceMessages(cur: SandboxMessage[], rows: SandboxMessage[]): SandboxMessage[] {
  const byId = new Map(rows.map((m) => [m.id, m]));
  return [...rows, ...cur.filter((m) => m.id < 0).filter(settled(byId))].sort((a, b) => order(a) - order(b));
}

/** How long after the fan's last message a reply is still expected. Before it reports a
 *  turn in progress the brain can spend tens of seconds just picking the message up
 *  (its dispatch queue, then the debounce window), so "not busy" alone means little. */
export const AWAIT_REPLY_MS = 3 * 60_000;

/** When the fan spoke last: 'queued' while a reply is still expected, 'unanswered' after. */
export function replyState(msgs: SandboxMessage[], now = Date.now()): 'queued' | 'unanswered' | null {
  let last: SandboxMessage | undefined;
  for (let i = msgs.length - 1; i >= 0 && !last; i -= 1) if (msgs[i]!.sender !== 'system') last = msgs[i];
  if (!last || last.sender !== 'fan') return null;
  return now - new Date(last.created_at).getTime() < AWAIT_REPLY_MS ? 'queued' : 'unanswered';
}

export const lastServerId = (msgs: SandboxMessage[]) =>
  msgs.reduce((max, m) => (m.id > max ? m.id : max), 0);

/* ── Notifications ───────────────────────────────────────────────────
   A notification row carries the Telegram message the operator would have got:
   HTML-ish text whose first line is the headline. Links point at the Sandbox Fan
   on the Platform, which doesn't exist, so those lines are dropped. */
const ENTITIES: Record<string, string> = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

export function notificationText(raw: unknown): { head: string; body: string[] } {
  const plain = String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&(amp|lt|gt|quot|#39);/g, (e) => ENTITIES[e] ?? e);
  const lines = plain.split('\n').map((l) => l.trim()).filter((l) => l && !/https?:\/\//.test(l));
  return { head: lines[0] ?? '', body: lines.slice(1) };
}

/* ── Time ────────────────────────────────────────────────────────── */
export function whenLabel(iso: string, yesterday: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diff === 1) return yesterday;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** A username the Platform would plausibly issue for the fan's display name. */
export const usernameOf = (name: string) =>
  'u_' + (name.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 20) || 'fan');
